import React, { useState } from "react";
import styled from "styled-components";

interface MaterialTextInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	value: string;
	placeholder?: string;
	maxLength?: number;
	onChange: (value: string) => void;
	color?: string;
	autoFocus?: boolean;
	maxWidth?: number;
}

export const MaterialTextInput: React.FC<MaterialTextInputProps> = ({
	value,
	placeholder,
	onChange,
	maxWidth,
	maxLength,
	autoCapitalize = "sentences",
	color,
	autoFocus,
	id,
	...htmlProps
}) => {
	const [focused, setFocused] = useState(false);

	return (
		<Container maxWidth={maxWidth} {...htmlProps} focused={focused} color={color}>
			{maxLength ? (
				<LetterCount color={color}>
					{value.length}/{maxLength}
				</LetterCount>
			) : null}
			<input
				id={id}
				data-id={id}
				type="text"
				autoCapitalize={autoCapitalize}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				placeholder={placeholder}
				autoFocus={autoFocus}
				value={value}
				onChange={(e) => {
					if (!maxLength || e.currentTarget.value.length <= maxLength) {
						onChange(e.currentTarget.value);
					}
				}}
				style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
			/>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["focused", "color", "maxWidth"].includes(prop),
})<{ focused: boolean; color?: string; maxWidth?: number }>`
	position: relative;
	input {
		width: 100%;
		min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		color: ${({ theme, color }) => color ?? theme.colors.textPrimary};
		font-size: 0.9375rem;
		border: none;
		outline: none;
		background-color: transparent;
		padding: 5px 0 9px;

		::placeholder {
			color: ${({ theme, color }) => color ?? theme.colors.textPrimary};
			font-size: ${({ maxWidth }) => (maxWidth ? `16px !important` : "0.9375rem")};
			opacity: 0.6;
			min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
			max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		}
	}

	&::after {
		content: "";
		width: 100%;
		min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		height: 2px;
		position: absolute;
		bottom: 0;
		left: 0;
		background-color: ${({ theme, color, focused }) => ((color ?? focused) ? "#206AF8" : theme.colors.textPrimary)};
		transform-origin: 0 50%;
		opacity: ${({ focused }) => (focused ? 1 : 0.5)};
		${({ focused }) => !focused && "transform: scaleX(.1)"};
		transition:
			opacity linear 0.2s,
			transform ease-in-out 0.2s;
	}
`;

const LetterCount = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "color",
})<{ color?: string }>`
	font-size: 0.5rem;
	color: ${({ theme, color }) => color ?? theme.colors.textPrimary};
	opacity: 0.5;
`;
